import { apiMutate, isApiError, parseApiErrorBody } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

// Mirrors the backend RegisterSnowflakeTablesRequest/Response contract for
// POST /api/catalog/admin/tables/register?schema=<schema>. Names are bare table
// names within that one schema, in Snowflake's own exact case.
export interface SyncSchemaTablesRequest {
  add: string[];
  remove: string[];
}

export interface SkippedSyncTable {
  table: string;
  reason: string;
  status_code: number;
}

export interface SyncSchemaTablesResponse {
  registered: string[];
  soft_deleted: string[];
  skipped_registrations: SkippedSyncTable[];
  skipped_soft_deletes: SkippedSyncTable[];
  errors: boolean;
}

const isSkippedTable = (value: unknown): value is SkippedSyncTable => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const { table, reason, status_code } = value as Record<string, unknown>;
  return typeof table === "string" && typeof reason === "string" && typeof status_code === "number";
};

const skipsFrom = (detail: Record<string, unknown>, field: string): SkippedSyncTable[] => {
  const skips = detail[field];
  return Array.isArray(skips) ? skips.filter(isSkippedTable) : [];
};

/**
 * When nothing at all succeeded the endpoint raises rather than returning, with
 * `detail = { message, skipped_registrations, skipped_soft_deletes }` instead of
 * the usual `{ detail: string }` envelope. Rebuild the normal response shape from
 * it so callers render a total failure through the same path as a partial one.
 * Returns null when the error isn't that shape (a 401, a gateway error, ...).
 */
const responseFromTotalFailure = (error: unknown): SyncSchemaTablesResponse | null => {
  if (!isApiError(error)) {
    return null;
  }
  const detail = parseApiErrorBody(error)?.detail;
  if (!detail || typeof detail !== "object") {
    return null;
  }

  const skipped_registrations = skipsFrom(
    detail as Record<string, unknown>,
    "skipped_registrations"
  );
  const skipped_soft_deletes = skipsFrom(detail as Record<string, unknown>, "skipped_soft_deletes");
  if (!skipped_registrations.length && !skipped_soft_deletes.length) {
    return null;
  }

  return {
    registered: [],
    soft_deleted: [],
    skipped_registrations,
    skipped_soft_deletes,
    errors: true,
  };
};

/**
 * Register and/or soft-delete tables in one schema. Resolves with the per-table
 * outcome for both a full and a partial success, and for the all-failed case the
 * endpoint reports by raising. Anything else (auth, network, a malformed body)
 * still throws, since there is no per-table outcome to show for it.
 */
export const syncSchemaTables = async (
  schema: string,
  request: SyncSchemaTablesRequest
): Promise<SyncSchemaTablesResponse> => {
  try {
    return await apiMutate<SyncSchemaTablesResponse>(
      `${VITE_CATALOG_API}/admin/tables/register?schema=${encodeURIComponent(schema)}`,
      "POST",
      request
    );
  } catch (e) {
    const totalFailure = responseFromTotalFailure(e);
    if (totalFailure) {
      return totalFailure;
    }
    throw e;
  }
};
